import React, { useCallback, useMemo } from 'react'
import LoadingDocument from '../../LoadingDocument';
import { date2DDMMMMMYYYString } from '../../../lib/textutils';
import '../../../styles/from-editor/text-styles.scss';
import { TDocument } from '../../types';
import { useDocuments } from '../../contexts/DekstopContext';

const DocumentBody = ({
  loading,
  doc,
  windowid,
}: {
  loading: boolean,
  doc?: TDocument,
  windowid: string,
}) => {

  const {
    anchorId,
    html,
    newtype,
    type_name,
    title,
    publish_nr,
    publish_year,
    publisher_name,
    publish_date,
    revisions,
    deleted,
    status_code,
    status_name,
    issue_year,
    issuers,
    revdata,
    domains
  } = doc || {};

  // republished acts keep the base name in `newtype` ("ORDIN"), the full one is in `type_name`
  const type = type_name || newtype;

  const { openLink } = useDocuments();

  // const actionString = useMemo(() => revdata
  //   ? revdata.rev_type === 'Publicare'
  //     ? revdata.rev_number === 1
  //       ? 'Publicare'
  //       : 'Republicare'
  //     : 'Modificare'
  //   : '', [revdata]);

  // const formattedRevDate = useMemo(() => revdata
  //   ? date2DDMMMMMYYYString(new Date(revdata.rev_date))
  //   : '', [revdata]);

  // document.title = `Lex - ${(title || '').replace(/<\/?[^>]+>/g, '')} - ${actionString} ${formattedRevDate}`

  const formattedPubDate = useMemo(() => publish_date
    ? date2DDMMMMMYYYString(new Date(publish_date))
    : ''
    , [publish_date]);

  const issuerTexts = issuers
    ? issuers.map(i => `${i.issuer_name} Nr. ${i.issuer_nr}/${issue_year}`)
    : []
  const singleIssuerNumber = issuers && issuers.length === 1
    ? ` Nr. ${issuers[0].issuer_nr}/${issue_year}`
    : ""

  const statusBadgeClass = useMemo(() => {
    switch (status_code) {
      case 'A':
      case 'I':
        return 'danger';
      case 'B':
      case 'L':
      case 'O':
      case 'v':
        return 'warning';
      case 'C':
      case 'M':
        return 'info';
      case 'V':
      case 'P':
      case 'R':
      case 'r':
        return 'success';
      default:
        return 'secondary';
    }
  }, [status_code]);

  const setTitleContent = useCallback((node: HTMLHeadingElement) => {
    if (node && title) {
      node.innerHTML = title
    }
  }, [title]);

  const setBodyContent = useCallback((node: HTMLDivElement) => {
    if (node && html) {
      let processed = html;
      while (processed.includes('  ')) {
        processed = processed.replace(/ {2}/g, ' &nbsp;')
      }

      // Custom parsing needed for Lege 147/2004
      // If content has newlines but no <br>/<p> tags, treat as plain text needing conversion
      if (processed.includes('\n') && !/<(br|p)\b/i.test(processed)) {
        const isTableLine = (line: string) =>
          line.includes('¦') || /^\s*\+[-+\s]*$/.test(line.trimEnd());

        const padAsciiTable = (tableLines: string[]): string => {
          // Find the border row with the most column separators (most + chars)
          const countPlus = (s: string) => (s.match(/\+/g) || []).length;
          const completeBorder = tableLines
            .filter(l => /^\+[-+]+[+¦]$/.test(l.trimEnd()))
            .reduce((best, l) => countPlus(l) > countPlus(best ?? '') ? l : best, null as string | null);
          if (!completeBorder) return tableLines.join('\n');

          const border = completeBorder.trimEnd();
          const colPositions: number[] = [];
          for (let i = 0; i < border.length; i++) {
            if (border[i] === '+') colPositions.push(i);
          }
          // If the border ends with ¦, include that position as the last column boundary
          if (border[border.length - 1] === '¦') {
            colPositions.push(border.length - 1);
          }
          if (colPositions.length < 2) return tableLines.join('\n');

          const expectedCols = colPositions.length - 1;
          const endsWithBar = border[border.length - 1] === '¦';

          const paddedLines = tableLines.map(line => {
            const trimmed = line.trimEnd();

            // Reconstruct border rows from colPositions
            if (/^\+[-+¦\s]*$/.test(trimmed)) {
              let row = '';
              for (let c = 0; c < colPositions.length - 1; c++) {
                row += '+' + '-'.repeat(colPositions[c + 1] - colPositions[c] - 1);
              }
              return row + (endsWithBar ? '+' : '+');
            }

            // Data rows
            if (trimmed.includes('¦')) {
              const dataCells = trimmed.split('¦').slice(1, -1);
              if (dataCells.length === expectedCols) {
                let row = '';
                for (let c = 0; c < expectedCols; c++) {
                  const width = colPositions[c + 1] - colPositions[c] - 1;
                  row += '¦' + dataCells[c].padEnd(width);
                }
                return row + '¦';
              }
            }

            return trimmed;
          });

          return paddedLines.join('\n');
        };

        const lines = processed.split('\n');
        let result = '';
        let tableLines: string[] = [];

        for (const line of lines) {
          if (isTableLine(line)) {
            tableLines.push(line);
          } else {
            if (tableLines.length > 0) {
              result += '<pre class="lex-plain-table">' + padAsciiTable(tableLines) + '</pre>';
              tableLines = [];
            }
            result += line + '<br>';
          }
        }
        if (tableLines.length > 0) {
          result += '<pre class="lex-plain-table">' + padAsciiTable(tableLines) + '</pre>';
        }

        processed = result;
      }

      node.innerHTML = processed.replace(/\t/g, "<span data-type='lex-custom-tab'>\t</span>");

      // Add event listeners to all links
      const listeners: { link: HTMLAnchorElement, cb: (e: MouseEvent) => void }[] = [];

      let relevantAnchor: HTMLAnchorElement | undefined;

      node.querySelectorAll('a').forEach(link => {
        const href = link.getAttribute('href') || '';

        // Inter-document links are rendered by the backend as `<host>/view/<docid>`.
        // The host part varies by environment: in dev it is `http://localhost:3000`,
        // but in the packaged Electron app the page is served from file://, so the
        // backend cannot resolve a usable origin and the href ends up as e.g.
        // `file://…/index.html#/view/<docid>` or even `null/view/<docid>`. Match the
        // `/view/<docid>` segment directly instead of a fixed origin prefix so the
        // links are handled in every environment. External links get target="_blank"
        // from the backend and must not be hijacked.
        const match = link.getAttribute('target') !== '_blank' ? href.match(/\/view\/([^/]+)/) : null;

        if (match) {
          let newDocId: string = '';
          let newAnchorId: string | undefined = undefined;

          if (match[1].includes("#")) {
            const [id, aId] = match[1].split('#');
            newDocId = id;
            newAnchorId = aId;
          } else {
            newDocId = match[1]
          }

          listeners.push({
            link,
            cb: (e: MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Opening', newDocId)
              openLink(windowid, newDocId, newAnchorId);
            }
          })
        } else {
          // if document has an anchorId - we should scroll that into view
          if (anchorId) {
            const anchorAttr = link.getAttribute('id');
            if (anchorAttr && anchorAttr === anchorId) {
              relevantAnchor = link;
            }
          }
        }
      });

      if (relevantAnchor) {
        relevantAnchor.scrollIntoView();
      }

      for (const entry of listeners) {
        entry.link.addEventListener('click', entry.cb);
      }

      return () => {
        for (const entry of listeners) {
          entry.link.removeEventListener('click', entry.cb);
        }
      };
    }
  }, [anchorId, html, openLink, windowid]);

  if (loading)
    return <div id="document-body"><LoadingDocument spin={loading} /></div>;

  if (!doc)
    return null;

  return (
    <div id="fulldoc">
      {deleted && <h2 className="hide4print" style={{ color: "red" }}>ACEST DOCUMENT ESTE MARCAT CA STERS IN BAZA DE DATE</h2>}
      <div className="lex-preview-content-header py-3 text-center hide4print">
        <div className="mb-2"><strong>{publisher_name} nr. {publish_nr}/{publish_year}</strong> din <strong>{formattedPubDate}</strong></div>
        <div className="font-weight-bold">{type} {singleIssuerNumber}<br />{issuerTexts.map((it, i) => <span className="badge badge-light" key={`issuer-${it}`}>{it}</span>)}</div>
      </div>
      <div className="lex-preview-content-header text-center hide4print">
        <div className="w-100 text-center mb-2">
          {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
          <h1 style={{ fontSize: 25 }} ref={setTitleContent}></h1>
        </div>
        <small>Stare: </small><span className={`badge badge-${statusBadgeClass}`}>{status_name}</span>
        <br />
        <small>Emitenti: <strong>{issuers?.map(i => i.issuer_name).join(', ')}</strong></small>
        <br />
        <small>Domenii: <strong>{domains?.map(d => d.domain_name).join(', ')}</strong></small>
        <br />
      </div>
      {
        revdata && revdata.rev_type === 'Modificare'
          ? <div className="lex-preview-content-headerxx w-100 text-center mb-2 pb-2 font-italic">
            {
              revdata.rev_type === 'Modificare' && revdata.rev_date === revisions?.[revisions.length - 1]?.rev_date
                ? 'ACT COMPUS - FORMA APLICABILA AZI!'
                : null
            }
            {/* Acesta este actul compus creat la data de <strong>{formattedRevDate}</strong> */}
          </div>
          : null
      }
      <div className="lex-preview-content-text data-lex-act-content">
        {/*reactified*/}
        <div id="document-body" className='document-body-class-for-reference-only' ref={setBodyContent} >
        </div>
      </div>
    </div>

  )
}

export default DocumentBody
